import React, { useState } from 'react';
import { User, Eye,  Clock, XCircle, AlertCircle, Calendar, UserCheck, Send, FileText } from 'lucide-react';
import Header from '../components/Header';
import AdminFooter from '../components/AdminFooter';
import { useGetDashboardQuery, useGetRecentApplicationsQuery } from '../store/services/dashboardApi';
import { useNavigate } from 'react-router-dom';
import StatusModal from '../components/StatusModal';
import { toast } from 'react-toastify';
import { useUpdateApplicationMutation } from '../store/services/applicationApi';
import DashboardBarChart from '../components/DashboardBarChart';

const Dashboard: React.FC = () => {

    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');

    const { data: dashboardData, isLoading: isLoadingDashboard, refetch: refetchDash } = useGetDashboardQuery(
        { startDate: startDate || undefined, endDate: endDate || undefined }
    );
    const { data: applicationsData, refetch: refetchApp } = useGetRecentApplicationsQuery();
    const [updateApplication] = useUpdateApplicationMutation();
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [application, setApplication] = useState({});

    const dashboardState = dashboardData?.data;
    const applicationsState = applicationsData?.data;

    
    const navigate = useNavigate();

    const handleClearDates = () => {
        setStartDate('');
        setEndDate('');
    };

    const getStatusColor = (status: string): string => {
        switch (status) {
            case 'applied':
            return 'bg-blue-100 text-blue-800';
            case 'scheduled':
            return 'bg-purple-100 text-purple-800';
            case 'selected':
            return 'bg-green-100 text-green-800';
            case 'under-review':
            return 'bg-yellow-100 text-yellow-800';
            case 'submitted':
            return 'bg-indigo-100 text-indigo-800';
            case 'rejected':
            return 'bg-red-100 text-red-800';
            default:
            return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusText = (status: string): string => {
        switch (status) {
            case 'applied':
            return 'Applied';
            case 'scheduled':
            return 'Scheduled';
            case 'selected':
            return 'Selected';
            case 'under-review':
            return 'Under Review';
            case 'submitted':
            return 'Submitted';
            case 'rejected':
            return 'Rejected';
            default:
            return status;
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'applied':
            return <Clock className="w-4 h-4" />;
            case 'scheduled':
            return <Calendar className="w-4 h-4" />;
            case 'selected':
            return <UserCheck className="w-4 h-4" />;
            case 'under-review':
            return <AlertCircle className="w-4 h-4" />;
            case 'submitted':
            return <Clock className="w-4 h-4" />;
            case 'rejected':
            return <XCircle className="w-4 h-4" />;
            default:
            return <Clock className="w-4 h-4" />;
        }
    };

    const formatDateTime = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            timeZone: 'Asia/Dhaka',
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
        });
    };

    const handleUpdateStatus = async (applicationId : string, updateData : { status: string; adminNotes?: string; rejectionReason?: string; remarkText?: string }) => {
        try {
            await updateApplication({
                id: applicationId,
                ...updateData,
            }).unwrap();
    
            toast.success(`Status updated to "${updateData.status.charAt(0).toUpperCase() + updateData.status.slice(1)}"`);
            await refetchApp();
            await refetchDash();
        } catch (err: any) {
            toast.error(`Failed to update status: ${err?.data?.message || err.message}`);
        }
    };

    const chartData = [
        { name: 'Applied', value: dashboardState?.appliedCount ?? 0, fill: '#60A5FA' },      
        { name: 'Scheduled', value: dashboardState?.scheduledCount ?? 0, fill: '#A78BFA' },  
        { name: 'Selected', value: dashboardState?.selectedCount ?? 0, fill: '#4ADE80' },    
        { name: 'Under Review', value: dashboardState?.underReviewCount ?? 0, fill: '#FACC15' }, 
        { name: 'Submitted', value: dashboardState?.submittedCount ?? 0, fill: '#818CF8' },  
        { name: 'Rejected', value: dashboardState?.rejectedCount ?? 0, fill: '#F87171' },   
    ];

  return (
    <div className="min-h-screen relative flex flex-col">
        <div className="bg-overlay"></div>
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 flex-1 w-full">
            <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                <h1 className="text-xl font-bold text-gray-900">Dashboard</h1>
                <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center">
                    <label className="text-sm font-medium text-gray-700">
                        Date Range:
                    </label>
                    <div className="flex w-full items-center gap-2 sm:w-auto">
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="admin-filter-date min-w-0 flex-1 sm:w-[170px] sm:flex-none"
                            placeholder="Start Date"
                        />
                        <span className="text-gray-500 self-center">to</span>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="admin-filter-date min-w-0 flex-1 sm:w-[170px] sm:flex-none"
                            placeholder="End Date"
                        />
                        {(startDate || endDate) && (
                            <button
                                onClick={handleClearDates}
                                className="shrink-0 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-800"
                            >
                                Clear
                            </button>
                        )}
                    </div>
                </div>
            </div>
            {isLoadingDashboard && (
                <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-5 sm:mb-6">
                <div className="grid grid-cols-2">
                    <div className="bg-white/50 shadow-sm p-4 border border-gray-200">
                        <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs sm:text-sm font-medium text-gray-600">Applied</p>
                            <p className="text-2xl font-bold text-blue-600">
                            {dashboardState?.appliedCount}
                            </p>
                        </div>
                        <div className="bg-blue-100 w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center">
                            <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                        </div>
                        </div>
                    </div>
                    <div className="bg-white/50 shadow-sm p-4 border border-gray-200">
                        <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs sm:text-sm font-medium text-gray-600">Submitted</p>
                            <p className="text-2xl font-bold text-indigo-600">
                            {dashboardState?.submittedCount}
                            </p>
                        </div>
                        <div className="bg-indigo-100 w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center">
                            <Send className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" />
                        </div>
                        </div>
                    </div>
                    <div className="bg-white/50 shadow-sm p-4 border border-gray-200">
                        <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs sm:text-sm font-medium text-gray-600">Scheduled</p>
                            <p className="text-2xl font-bold text-purple-600">
                            {dashboardState?.scheduledCount}
                            </p>
                        </div>
                        <div className="bg-purple-100 w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center">
                            <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
                        </div>
                        </div>
                    </div>
                    <div className="bg-white/50 shadow-sm p-4 border border-gray-200">
                        <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs sm:text-sm font-medium text-gray-600">Under Review</p>
                            <p className="text-2xl font-bold text-yellow-600">
                            {dashboardState?.underReviewCount}
                            </p>
                        </div>
                        <div className="bg-yellow-100 w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center">
                            <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600" />
                        </div>
                        </div>
                    </div>
                    <div className="bg-white/50 shadow-sm p-4 border border-gray-200">
                        <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs sm:text-sm font-medium text-gray-600">Selected</p>
                            <p className="text-2xl font-bold text-green-700">
                            {dashboardState?.selectedCount}
                            </p>
                        </div>
                        <div className="bg-green-100 w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center">
                            <UserCheck className="w-5 h-5 sm:w-6 sm:h-6 text-green-700" />
                        </div>
                        </div>
                    </div>
                    <div className="bg-white/50 shadow-sm p-4 border border-gray-200">
                        <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs sm:text-sm font-medium text-gray-600">Rejected</p>
                            <p className="text-2xl font-bold text-red-600">
                            {dashboardState?.rejectedCount}
                            </p>
                        </div>
                        <div className="bg-red-100 w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center">
                            <XCircle className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
                        </div>
                        </div>
                    </div>
                </div>
                <div className="bg-white/50 shadow-sm border border-gray-200">
                    <DashboardBarChart data={chartData} />
                </div>
            </div>
            
            <div className="mb-6 flex items-center justify-between gap-3">
                <h1 className="min-w-0 text-xl font-bold text-gray-900">Recent Application</h1>
                <button
                    type="button"
                    onClick={() => navigate('/admin/applications')}
                    className="flex shrink-0 items-center rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-700 transition duration-200 hover:bg-indigo-100"
                >
                    <FileText className="w-4 h-4 mr-1" />
                    See All
                </button>
            </div>
            <div className="bg-white/50 rounded-xl shadow-sm border border-gray-200">
                <div className="p-4 sm:p-6">
                    <div className="space-y-4 overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Applicant
                                </th>
                                <th className="px-4 sm:px-6 py-3 sm:py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider  md:table-cell">
                                    Phone Number
                                </th>
                                <th className="px-4 sm:px-6 py-3 sm:py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider  lg:table-cell">
                                    Applied Date
                                </th>
                                <th className="px-4 sm:px-6 py-3 sm:py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-4 sm:px-6 py-3 sm:py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white/50 divide-y divide-gray-200">
                                {applicationsState?.map((app : any) => (
                                    <tr key={app._id} className="hover:bg-gray-50 transition duration-200">
                                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="bg-indigo-100 w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                                                    {app.profileImage ? (
                                                        <img src={app.profileImage} alt="Applicant" className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover"/>
                                                    ) : (
                                                        <User className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />
                                                    )}
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <div className="text-sm font-medium text-left text-gray-900 truncate">{app.name}</div>
                                                    <div className="text-sm text-gray-500 text-left truncate">{app.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap md:table-cell">
                                            <div className="text-sm text-gray-900 text-center">{app.phone}</div>
                                        </td>
                                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap lg:table-cell">
                                            <div className="text-sm text-gray-900 text-center">{formatDateTime(app.submittedDate)}</div>
                                        </td>
                                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                                            <div className=" flex justify-center items-center">
                                                <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(app.status)} flex items-center space-x-1 w-fit`}>
                                                    {getStatusIcon(app.status)}
                                                    <span className="sm:inline">{getStatusText(app.status)}</span>
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <div className="flex flex-col justify-end sm:flex-row space-y-1 sm:space-y-0 sm:space-x-2">
                                                <button
                                                    onClick={() => navigate(`/admin/applications/${app._id}`)}
                                                    className="bg-indigo-100 text-indigo-700 hover:bg-indigo-200  p-1 rounded-md text-xs font-medium transition duration-200 flex items-center justify-center space-x-1"
                                                >
                                                <Eye className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setApplication(app)
                                                        setStatusModalOpen(!statusModalOpen)
                                                    }}
                                                    className={`p-1 rounded-md text-xs font-medium transition duration-200 flex items-center justify-center space-x-1 ${getStatusColor(app.status)}`}
                                                >
                                                    {getStatusIcon(app.status)}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
        <StatusModal
            isOpen={statusModalOpen}
            onClose={() => setStatusModalOpen(false)}
            application={application}
            onUpdateStatus={handleUpdateStatus}
        />
        <AdminFooter />
    </div>
  );
};

export default Dashboard;
